// 콘텐츠 타입
export type TContentType =
	| 'ig_feed'
	| 'ig_video'
	| 'yt_video'
	| 'yt_shorts'
	| 'tt_content';

// 플랫폼 타입
export type TTrackingPlatform = 'INSTAGRAM' | 'YOUTUBE' | 'TIKTOK' | 'THREADS';

// 수집 상태
export type TCollectStatus = 'COLLECTING' | 'COLLECT_COMPLETED';

// 콘텐츠 타입 한국어 매핑
export const CONTENT_TYPE_MAP: Record<string, string> = {
	ig_feed: '인스타그램 피드',
	ig_video: '인스타그램 릴스',
	yt_video: '유튜브 영상(롱폼)',
	yt_shorts: '유튜브 쇼츠',
	tt_content: '틱톡',
};

// 플랫폼 한국어 매핑
export const PLATFORM_TYPE_MAP: Record<string, string> = {
	INSTAGRAM: '인스타그램',
	YOUTUBE: '유튜브',
	TIKTOK: '틱톡',
	THREADS: '스레드',
};

// 수집 상태 한국어 매핑
export const COLLECT_STATUS_MAP: Record<TCollectStatus, string> = {
	COLLECTING: '수집 중',
	COLLECT_COMPLETED: '수집 완료',
};

// 콘텐츠 타입별 색상
export const CONTENT_TYPE_COLOR_MAP: Record<string, string> = {
	ig_video: '#FF5452',
	ig_feed: '#FF9391',
	yt_video: '#DEE2E6',
	yt_shorts: '#FFD2D1',
	tt_content: '#868E96',
};

// 트래킹 목록 아이템
export interface ITrackingDB {
	code: string;
	name: string;
	collectStatus: TCollectStatus;
	contentCount: number;
	contentTypes: TContentType[];
	trackingStartDate: string;
	trackingEndDate: string;
}

// 트래킹 요약 (Section 1)
export interface ITrackingSummaryDB {
	participantCreatorCount: number;
	trackingStartDate: string;
	trackingEndDate: string;
	trackingDays: number;
	contentCount: number;
	trendingContentCount: number;
	totalViewCount: number;
	collectionRetentionDays: number | null;
}

// 콘텐츠 유형 분포
export interface IContentTypeDistributionDB {
	type: string;
	label: string;
	count: number;
	percentage: number;
}

// 성과 지표
export interface IPerformanceDB {
	likeCount: number;
	commentCount: number;
	viewCount: number;
	shareCount: number;
	engagementCount: number;
}

// 콘텐츠 요약 (Section 2)
export interface IContentSummaryDB {
	contentTypeDistribution: IContentTypeDistributionDB[];
	averagePerformance: IPerformanceDB;
	totalPerformance: IPerformanceDB;
}

// 성과 추이 아이템 (Section 3)
export interface ITrendItemDB {
	date: string;
	value: number;
	contentCount: number;
	newContentCount: number;
	trendingContentCount: number;
	changeRate: number | null;
	changeDirection: 'UP' | 'DOWN' | 'SAME' | null;
}

// 하이라이트 콘텐츠 (Section 3)
export interface IHighlightContentDB {
	rank: number;
	contentCode: string;
	influencerCode: string;
	thumbnailUrl: string;
	platformType: TTrackingPlatform;
	contentType: TContentType;
	username: string;
	profileImageUrl: string;
	contentTitle: string | null;
	contentDescription: string | null;
	followerCount: number;
	uploadDate: string;
	contentLink: string;
	viewCount: number;
	likeCount: number;
	commentCount: number;
	shareCount: number;
	saveCount: number | null;
	engagementCount: number;
	videoDurationSeconds: number | null;
}

// 콘텐츠 랭킹 (Section 4)
export interface IContentRankingDB {
	rank: number;
	contentCode: string;
	influencerCode: string;
	thumbnailUrl: string;
	platformType: TTrackingPlatform;
	contentType: TContentType;
	contentTitle: string | null;
	contentDescription: string | null;
	uploadDate: string;
	contentLink: string;
	username: string;
	profileImageUrl: string;
	followerCount: number;
	viewCount: number;
	likeCount: number;
	commentCount: number;
	shareCount: number;
	saveCount: number | null;
	engagementCount: number;
	videoDurationSeconds: number | null;
	viewCountsDisabled?: boolean;
	likeCountsDisabled?: boolean;
	commentsDisabled?: boolean;
}

// 콘텐츠 랭킹 응답 (페이지네이션)
export interface IContentRankingResponseDB {
	contents: IContentRankingDB[];
	totalCount: number;
	page: number;
	pageSize: number;
}

// 크리에이터 랭킹 (Section 4)
export interface ITrackingCreatorRankingDB {
	rank: number;
	platformType: TTrackingPlatform;
	platformPk: string;
	username: string;
	nickname: string | null;
	profileImageUrl: string;
	followerCount: number;
	contentCount: number;
	contentTypes: TContentType[];
	viewCount: number;
	likeCount: number;
	commentCount: number;
	shareCount: number;
	engagementCount: number;
	maxViewCount: number;
	influencerCode: string;
	reportLink: string | null;
}

// 크리에이터 랭킹 응답 (페이지네이션)
export interface ICreatorRankingResponseDB {
	creators: ITrackingCreatorRankingDB[];
	totalCount: number;
	page: number;
	pageSize: number;
}

// 급상승 콘텐츠
export interface ITrendingContentDB {
	rank: number;
	contentCode: string;
	influencerCode: string;
	thumbnailUrl: string;
	platformType: TTrackingPlatform;
	contentType: TContentType;
	username: string;
	profileImageUrl: string;
	contentTitle: string | null;
	contentDescription: string | null;
	uploadDate: string;
	contentLink: string;
	viewCount: number;
	likeCount: number;
	commentCount: number;
	shareCount: number;
	saveCount: number | null;
	engagementCount: number;
	videoDurationSeconds: number | null;
}

// 급상승 콘텐츠 응답 (페이지네이션)
export interface ITrendingContentResponseDB {
	contents: ITrendingContentDB[];
	totalCount: number;
	page: number;
	pageSize: number;
}

// 크리에이터별 콘텐츠
export interface ITrackingCreatorContentDB {
	contentCode: string;
	influencerCode: string;
	thumbnailUrl: string;
	contentType: TContentType;
	contentTitle: string | null;
	contentDescription: string | null;
	uploadDate: string;
	contentLink: string;
	viewCount: number;
	likeCount: number;
	commentCount: number;
	shareCount: number;
	engagementCount: number;
	videoDurationSeconds: number | null;
	viewCountsDisabled?: boolean;
	likeCountsDisabled?: boolean;
	commentsDisabled?: boolean;
}

// 필터용 계정/채널 목록
export interface IInfluencerDB {
	influencerCode: string;
	accountCode: string;
	username: string;
	profileImageUrl: string | null;
	platformType: TTrackingPlatform;
}

// 추이 API 파라미터
export interface ITrendParams {
	metric: 'VIEW' | 'LIKE' | 'COMMENT' | 'SHARE' | 'ENGAGEMENT';
	aggregationType: 'DAILY' | 'CUMULATIVE';
	contentTypes?: TContentType[];
	startDate?: string;
	endDate?: string;
}

// 하이라이트 API 파라미터
export interface IHighlightParams {
	metric: 'VIEW' | 'LIKE' | 'COMMENT' | 'SHARE' | 'ENGAGEMENT';
	contentTypes?: TContentType[];
	date?: string;
	limit?: number;
}

// 랭킹 공통 필터 파라미터
export interface IRankingFilterParams {
	startDate?: string;
	endDate?: string;
	contentTypes?: TContentType[];
	followerMin?: number;
	followerMax?: number;
	influencerCode?: string[];
	orderBy?: string;
	page?: number;
	pageSize?: number;
}

// 크리에이터 랭킹 필터 파라미터
export interface ICreatorRankingFilterParams extends IRankingFilterParams {
	viewMode?: 'AVERAGE' | 'TOTAL';
	metric?: string;
}

// 급상승 콘텐츠 API 파라미터
export interface ITrendingContentParams {
	metric?: 'VIEW' | 'LIKE' | 'COMMENT' | 'SHARE';
	contentTypes?: TContentType[];
	startDate?: string;
	endDate?: string;
	page?: number;
	pageSize?: number;
}
